<script setup>
/* eslint-disable */
import { ref, reactive, onMounted, computed, watch } from 'vue';
import api from '@/services/api';
import Swal from 'sweetalert2';
import logo from "@/assets/img/fondotam-blanco.png";

// --- ESTADOS Y DATOS ---
const usuarios = ref([]);
const mensajeUsuarios = ref('');

// --- LISTAS PARA SELECTORES ---
const regionales = ref([]);
const gerencias = ref([]);
const secciones = ref([]); // NUEVO: Estado para Secciones
const cargos = ref([]);

const EXTENSIONES = [
  { value: 'LP', label: 'La Paz' },
  { value: 'OR', label: 'Oruro' },
  { value: 'PT', label: 'Potosí' },
  { value: 'CB', label: 'Cochabamba' },
  { value: 'SC', label: 'Santa Cruz' },
  { value: 'BN', label: 'Beni' },
  { value: 'PA', label: 'Pando' },
  { value: 'TJ', label: 'Tarija' },
  { value: 'CH', label: 'Chuquisaca' },
];

const ROLES = [
  { value: 'admin_informatico', label: 'Administrador Informático' },
  { value: 'admin_principal', label: 'Administrador Principal' },
  { value: 'admin_regional', label: 'Administrador Regional' },
  { value: 'usuario', label: 'Usuario' },
  { value: 'consultor', label: 'Consultor' },
];

// --- FILTROS Y PAGINACIÓN ---
const filtros = reactive({
  busqueda: '',
  estado: ''
});

const paginacion = reactive({
  paginaActual: 1,
  itemsPorPagina: 8
});

// --- MODAL DE EDICIÓN ---
const modalEdicion = ref(false);
const guardando = ref(false);
const cargandoUsuario = ref(false);
const archivoRespaldoNuevo = ref(null);
const usuarioEditando = reactive({
  id: null,
  username: '',
  first_name: '',
  last_name: '',
  email: '',
  is_active: true,
  is_staff: false,
  rol: 'usuario',
  regional_id: null,
  gerencia_id: null,
  seccion_id: null, // NUEVO
  cargo_id: null,
  ci: '',
  extension: '',
  telefono: '',
  documento_respaldo_general: null
});

const handleRespaldoChange = (e) => {
  archivoRespaldoNuevo.value = e.target.files[0] || null;
};

// --- CARGA DE DATOS ---
const obtenerUsuarios = async () => {
  mensajeUsuarios.value = '';
  try {
    const response = await api.get('users/');
    usuarios.value = response.data.results || response.data;
  } catch (error) {
    mensajeUsuarios.value = 'Error al cargar los usuarios: ' + (error.response?.data?.detail || error.message);
  }
};

const cargarRegionales = async () => {
  try {
    const { data } = await api.get('regionales/');
    regionales.value = data.results || data;
  } catch (error) { console.error('Error al cargar regionales:', error); }
};

const cargarGerencias = async (regionalId) => {
  if (!regionalId) { gerencias.value = []; return; }
  try {
    const { data } = await api.get(`gerencias/?regional=${regionalId}`);
    gerencias.value = data.results || data;
  } catch (error) { console.error('Error al cargar gerencias:', error); }
};

const cargarSecciones = async (gerenciaId) => {
  if (!gerenciaId) { secciones.value = []; return; }
  try {
    const { data } = await api.get(`secciones/?gerencia=${gerenciaId}`);
    secciones.value = data.results || data;
  } catch (error) { console.error('Error al cargar secciones:', error); }
};

const cargarCargos = async (seccionId) => {
  if (!seccionId) { cargos.value = []; return; }
  try {
    const { data } = await api.get(`cargos/?seccion=${seccionId}`); // CAMBIO: Ahora filtra por sección
    cargos.value = data.results || data;
  } catch (error) { console.error('Error al cargar cargos:', error); }
};

// --- CASCADING COMPLETAMENTE ACTUALIZADO ---
const onRegionalChange = () => {
  usuarioEditando.gerencia_id = null;
  usuarioEditando.seccion_id = null;
  usuarioEditando.cargo_id = null;
  secciones.value = [];
  cargos.value = [];
  cargarGerencias(usuarioEditando.regional_id);
};

const onGerenciaChange = () => {
  usuarioEditando.seccion_id = null;
  usuarioEditando.cargo_id = null;
  cargos.value = [];
  cargarSecciones(usuarioEditando.gerencia_id);
};

const onSeccionChange = () => {
  usuarioEditando.cargo_id = null;
  cargarCargos(usuarioEditando.seccion_id);
};

onMounted(() => {
  obtenerUsuarios();
  cargarRegionales();
});

// --- LÓGICA COMPUTADA (FILTROS Y PAGINACIÓN) ---
const nombreCompleto = (user) => {
  const nombre = `${user.first_name || ''} ${user.last_name || ''}`.trim();
  return nombre || user.username;
};

const obtenerLabelRol = (rolValue) => {
  const rol = ROLES.find(r => r.value === rolValue);
  return rol ? rol.label : 'Usuario';
};

const usuariosFiltrados = computed(() => {
  return usuarios.value.filter(user => {
    const termino = filtros.busqueda.toLowerCase();
    const coincideTexto = !termino ||
      nombreCompleto(user).toLowerCase().includes(termino) ||
      (user.email && user.email.toLowerCase().includes(termino)) ||
      (user.username && user.username.toLowerCase().includes(termino));

    let coincideEstado = true;
    if (filtros.estado !== '') {
      const isActivoFiltro = filtros.estado === 'activo';
      coincideEstado = user.is_active === isActivoFiltro;
    }

    return coincideTexto && coincideEstado;
  });
});

const totalPaginas = computed(() => {
  return Math.ceil(usuariosFiltrados.value.length / paginacion.itemsPorPagina) || 1;
});

const paginasVisibles = computed(() => {
  const maxVisibles = 5;
  const total = totalPaginas.value;
  const actual = paginacion.paginaActual;

  let inicio = Math.max(1, actual - Math.floor(maxVisibles / 2));
  let fin = Math.min(total, inicio + maxVisibles - 1);

  if (fin - inicio + 1 < maxVisibles) {
    inicio = Math.max(1, fin - maxVisibles + 1);
  }

  const paginas = [];
  for (let i = inicio; i <= fin; i++) {
    paginas.push(i);
  }
  return paginas;
});

const usuariosPaginados = computed(() => {
  const inicio = (paginacion.paginaActual - 1) * paginacion.itemsPorPagina;
  const fin = inicio + paginacion.itemsPorPagina;
  return usuariosFiltrados.value.slice(inicio, fin);
});

const cambiarPagina = (pagina) => {
  if (pagina >= 1 && pagina <= totalPaginas.value) {
    paginacion.paginaActual = pagina;
  }
};

// --- FUNCIONES DEL MODAL ---
const abrirModalEdicion = async (userId) => {
  try {
    cargandoUsuario.value = true;
    const { data } = await api.get(`users/${userId}/`);
    Object.assign(usuarioEditando, {
      id: data.id,
      username: data.username || '',
      first_name: data.first_name || '',
      last_name: data.last_name || '',
      email: data.email || '',
      is_active: data.is_active !== undefined ? data.is_active : true,
      is_staff: data.is_staff !== undefined ? data.is_staff : false,
      rol: data.rol || 'usuario',
      regional_id: data.regional_id || null,
      gerencia_id: data.gerencia_id || null,
      seccion_id: data.seccion_id || null, // Asignamos la sección si el backend la envía
      cargo_id: data.cargo_id || null,
      ci: data.ci || '',
      extension: data.extension || '',
      telefono: data.telefono || '',
      documento_respaldo_general: data.documento_respaldo_general || null,
    });

    // Cargar listas en cascada para los valores actuales preseleccionados
    if (data.regional_id) await cargarGerencias(data.regional_id);
    if (data.gerencia_id) await cargarSecciones(data.gerencia_id);
    if (data.seccion_id) await cargarCargos(data.seccion_id);

    modalEdicion.value = true;
  } catch (error) {
    // Error manejado globalmente
  } finally {
    cargandoUsuario.value = false;
  }
};

const guardarEdicion = async () => {
  if (!usuarioEditando.username.trim()) {
    Swal.fire('Atención', 'El nombre de usuario es obligatorio.', 'warning');
    return;
  }
  guardando.value = true;
  try {
    const fd = new FormData();
    fd.append('username', usuarioEditando.username);
    fd.append('first_name', usuarioEditando.first_name);
    fd.append('last_name', usuarioEditando.last_name);
    fd.append('email', usuarioEditando.email);
    fd.append('is_active', usuarioEditando.is_active);
    fd.append('is_staff', usuarioEditando.is_staff);
    if (usuarioEditando.rol) fd.append('rol', usuarioEditando.rol);
    if (usuarioEditando.regional_id) fd.append('regional_id', usuarioEditando.regional_id);
    if (usuarioEditando.gerencia_id) fd.append('gerencia_id', usuarioEditando.gerencia_id);
    if (usuarioEditando.seccion_id) fd.append('seccion_id', usuarioEditando.seccion_id);
    if (usuarioEditando.cargo_id) fd.append('cargo_id', usuarioEditando.cargo_id);
    if (usuarioEditando.ci) fd.append('ci', usuarioEditando.ci);
    if (usuarioEditando.extension) fd.append('extension', usuarioEditando.extension);
    if (usuarioEditando.telefono) fd.append('telefono', usuarioEditando.telefono);

    if (archivoRespaldoNuevo.value) {
      fd.append('documento_respaldo_general', archivoRespaldoNuevo.value);
    }

    await api.patch(`users/${usuarioEditando.id}/`, fd);
    Swal.fire({ title: '¡Actualizado!', text: 'El usuario fue modificado exitosamente.', icon: 'success', timer: 2000, showConfirmButton: false });
    modalEdicion.value = false;
    archivoRespaldoNuevo.value = null;
    await obtenerUsuarios();
  } catch (error) {
    // Error manejado globalmente
  } finally {
    guardando.value = false;
  }
};

const toggleActivacion = async (user) => {
  const accion = user.is_active ? 'desactivar' : 'activar';
  const resultado = await Swal.fire({
    title: `¿${accion.charAt(0).toUpperCase() + accion.slice(1)} usuario?`,
    text: `${nombreCompleto(user)} será ${user.is_active ? 'desactivado y no podrá acceder al sistema' : 'activado y podrá iniciar sesión'}.`,
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: `Sí, ${accion}`,
    cancelButtonText: 'Cancelar',
    confirmButtonColor: user.is_active ? '#dc3545' : '#28a745',
  });

  if (resultado.isConfirmed) {
    try {
      await api.patch(`users/${user.id}/`, { is_active: !user.is_active });
      await obtenerUsuarios();
      Swal.fire({ title: '¡Listo!', text: `Usuario ${accion}do exitosamente.`, icon: 'success', timer: 1500, showConfirmButton: false });
    } catch (error) {
      // Error manejado globalmente
    }
  }
};

const cerrarModal = () => { modalEdicion.value = false; };
</script>

<template>
  <div class="py-4 app-transition">
    <div class="card card-custom shadow-xl border-0 border-radius-xl overflow-hidden">
      
      <div class="header-tamep d-flex flex-column flex-md-row justify-content-between align-items-center px-4 py-3 border-radius-top">
        <div class="d-flex align-items-center mb-3 mb-md-0">
          <div class="logo-container-left me-3"><img :src="logo" class="logo-img"></div>
          <div class="border-start ps-3 border-white opacity-8 text-start">
            <h6 class="mb-0 text-white text-uppercase font-weight-bolder">Directorio de Usuarios</h6>
            <small class="text-white opacity-7">Gestión de accesos, roles y ubicación organizacional.</small>
          </div>
        </div>
        <div class="d-flex align-items-center">
          <span class="badge bg-white text-azul-tamep px-3 py-2 shadow-sm" style="border-radius: 8px;">
            <i class="fas fa-users me-1"></i> {{ usuariosFiltrados.length }} Registros Activos
          </span>
        </div>
      </div>

      <div class="p-4 bg-section-custom border-bottom">
        <div class="row g-3">
          <div class="col-md-8">
            <div class="input-group input-group-lg shadow-sm">
              <span class="input-group-text bg-white bg-input-custom border-end-0 text-primary">
                <i class="fas fa-search"></i>
              </span>
              <input type="text" v-model="filtros.busqueda" class="form-control input-custom border-start-0 ps-0" 
                placeholder="Buscar por nombre, apellidos, correo o usuario..." 
                @input="paginacion.paginaActual = 1">
            </div>
          </div>
          <div class="col-md-4">
            <div class="input-group input-group-lg shadow-sm">
              <span class="input-group-text bg-white bg-input-custom border-end-0 text-primary">
                <i class="fas fa-filter"></i>
              </span>
              <select v-model="filtros.estado" class="form-select input-custom border-start-0 ps-0 text-secondary" @change="paginacion.paginaActual = 1">
                <option value="">Todos los Estados</option>
                <option value="activo">Cuentas Activas</option>
                <option value="inactivo">Cuentas Inactivas</option>
              </select>
            </div>
          </div>
        </div>
      </div>

    <div v-if="mensajeUsuarios" class="alert alert-danger mx-4 mt-4 text-white text-sm" role="alert">
      <i class="fas fa-exclamation-triangle me-2"></i> {{ mensajeUsuarios }}
    </div>

    <div class="card-body card-body-custom px-0 pt-0 pb-2">
      <div class="table-responsive p-0 min-vh-50">
        <table class="table align-items-center mb-0 table-hover table-custom">
          <thead>
            <tr>
              <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Funcionario</th>
              <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Cargo Institucional</th>
              <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Rol del Sistema</th>
              <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Fecha Registro</th>
              <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Estado</th>
              <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Opciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="usuariosPaginados.length === 0">
              <td colspan="6" class="text-center py-5">
                <div class="d-flex flex-column align-items-center justify-content-center opacity-5">
                  <i class="fas fa-user-slash fa-3x mb-3 text-secondary text-custom-muted"></i>
                  <h6 class="text-secondary text-custom-muted">No se encontraron usuarios</h6>
                  <p class="text-sm text-muted text-custom-muted">Intente ajustar los filtros de búsqueda.</p>
                </div>
              </td>
            </tr>
            <tr v-for="user in usuariosPaginados" :key="user.id" class="transition-row row-custom">
              <td>
                <div class="d-flex px-3 py-2 align-items-center">
                  <div class="d-flex flex-column justify-content-center">
                    <h6 class="mb-0 text-sm font-weight-bold text-dark text-title-custom">{{ nombreCompleto(user) }}</h6>
                    <p class="text-xs text-secondary text-custom-muted mb-0 mt-1"><i class="fas fa-envelope me-1 opacity-7"></i> {{ user.email || 'Sin correo' }}</p>
                  </div>
                </div>
              </td>
              <td>
                <p class="text-sm font-weight-bold mb-0 text-dark text-title-custom">{{ user.cargo_nombre || 'Cargo no asignado' }}</p>
                <p class="text-xs text-secondary text-custom-muted mb-0"><i class="fas fa-map-marker-alt me-1 text-danger opacity-7"></i> {{ user.regional_nombre || 'Regional no definida' }}</p>
              </td>
              <td>
                <span class="badge bg-light bg-badge-custom text-dark border border-custom shadow-none text-xs">
                  <i class="fas fa-shield-alt me-1 text-primary"></i> {{ obtenerLabelRol(user.rol) }}
                </span>
              </td>
              <td class="align-middle text-center">
                <span class="text-secondary text-custom-muted text-xs font-weight-bold">
                  <i class="far fa-calendar-alt me-1"></i> {{ user.date_joined ? new Date(user.date_joined).toLocaleDateString('es-ES') : '---' }}
                </span>
              </td>
              <td class="align-middle text-center text-sm">
                <div @click="toggleActivacion(user)" class="cursor-pointer d-inline-block" :title="user.is_active ? 'Clic para Suspender' : 'Clic para Activar'">
                  <span class="badge badge-sm px-3 py-2" :class="user.is_active ? 'bg-gradient-success' : 'bg-gradient-secondary'">
                    <i class="fas" :class="user.is_active ? 'fa-check-circle' : 'fa-ban'"></i> 
                    {{ user.is_active ? ' Activo' : ' Suspendido' }}
                  </span>
                </div>
              </td>
              <td class="align-middle text-center">
                <button @click="abrirModalEdicion(user.id)" class="btn btn-outline-primary btn-outline-custom btn-sm mb-0 px-3 hover-lift">
                  <i class="fas fa-user-edit me-1"></i> Gestionar
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="d-flex flex-column flex-md-row justify-content-between align-items-center px-4 pt-4 pb-3 border-top border-custom-divider bg-light bg-pagination-custom">
        <span class="text-sm text-secondary text-custom-muted mb-2 mb-md-0 font-weight-bold">
          Mostrando {{ (paginacion.paginaActual - 1) * paginacion.itemsPorPagina + (usuariosFiltrados.length > 0 ? 1 : 0) }} al
          {{ Math.min(paginacion.paginaActual * paginacion.itemsPorPagina, usuariosFiltrados.length) }}
          de {{ usuariosFiltrados.length }} usuarios
        </span>
        <ul class="pagination pagination-sm pagination-primary m-0 align-items-center">
          <li class="page-item" :class="{ disabled: paginacion.paginaActual === 1 }">
            <button class="page-link" @click="cambiarPagina(1)" title="Primera Página">
              <i class="fas fa-angle-double-left"></i>
            </button>
          </li>
          <li class="page-item" :class="{ disabled: paginacion.paginaActual === 1 }">
            <button class="page-link" @click="cambiarPagina(paginacion.paginaActual - 1)" title="Página Anterior">
              <i class="fas fa-angle-left"></i>
            </button>
          </li>

          <li class="page-item disabled d-none d-sm-block" v-if="paginasVisibles.length && paginasVisibles[0] > 1">
            <span class="page-link border-0">...</span>
          </li>

          <li class="page-item" v-for="pag in paginasVisibles" :key="pag" :class="{ active: pag === paginacion.paginaActual }">
            <button class="page-link font-weight-bold" @click="cambiarPagina(pag)">{{ pag }}</button>
          </li>

          <li class="page-item disabled d-none d-sm-block" v-if="paginasVisibles.length && paginasVisibles[paginasVisibles.length - 1] < totalPaginas">
            <span class="page-link border-0">...</span>
          </li>

          <li class="page-item" :class="{ disabled: paginacion.paginaActual === totalPaginas }">
            <button class="page-link" @click="cambiarPagina(paginacion.paginaActual + 1)" title="Página Siguiente">
              <i class="fas fa-angle-right"></i>
            </button>
          </li>
          <li class="page-item" :class="{ disabled: paginacion.paginaActual === totalPaginas }">
            <button class="page-link" @click="cambiarPagina(totalPaginas)" :title="`Última Página (${totalPaginas})`">
              <i class="fas fa-angle-double-right"></i>
            </button>
          </li>
        </ul>
      </div>
    </div>
  </div>
  </div>

  <div v-if="modalEdicion" class="modal-overlay" @click.self="cerrarModal">
    <div class="card shadow-2xl modal-card modal-card-custom animate__animated animate__fadeInUp animate__faster border-0">
      
      <div class="card-header modal-header-custom p-4 border-bottom d-flex justify-content-between align-items-center border-radius-top-xl">
        <div class="d-flex align-items-center">
          <div class="icon-box-modal bg-azul-tamep text-white me-3 shadow-sm">
            <i class="fas fa-user-cog fa-lg"></i>
          </div>
          <div>
            <h5 class="mb-0 text-title-custom font-weight-bolder">Configuración de Usuario</h5>
            <p class="text-xs mb-0 text-custom-muted">ID de Sistema: @{{ usuarioEditando.username }}</p>
          </div>
        </div>
        <button class="btn btn-icon-only btn-outline-secondary mb-0 border-0 rounded-circle btn-close-modal" @click="cerrarModal">
          <i class="fas fa-times fa-lg"></i>
        </button>
      </div>

      <div class="card-body modal-body-custom modal-body-scroll p-4">
        <div v-if="cargandoUsuario" class="text-center py-5">
          <div class="spinner-border text-primary" role="status"></div>
          <p class="mt-2 text-sm text-custom-muted">Cargando perfil...</p>
        </div>

        <div v-else class="row g-4">
          
          <div class="col-lg-6">
            <div class="card border border-custom-divider shadow-none mb-4 modal-section-card">
              <div class="card-body p-3">
                <h6 class="text-uppercase text-xs font-weight-bolder text-primary mb-3 d-flex align-items-center gap-2"><i class="fas fa-id-card"></i> Identidad</h6>
                
                <div class="row g-2">
                  <div class="col-md-6 mb-2">
                    <label class="form-label text-xs font-weight-bold mb-1 text-custom-muted">Nombres</label>
                    <input type="text" class="form-control input-custom" v-model="usuarioEditando.first_name" placeholder="Nombres">
                  </div>
                  <div class="col-md-6 mb-2">
                    <label class="form-label text-xs font-weight-bold mb-1 text-custom-muted">Apellidos</label>
                    <input type="text" class="form-control input-custom" v-model="usuarioEditando.last_name" placeholder="Apellidos">
                  </div>
                  
                  <div class="col-8 mb-2">
                    <label class="form-label text-xs font-weight-bold mb-1 text-custom-muted">Cédula de Identidad</label>
                    <div class="input-group input-group-custom">
                      <span class="input-group-text bg-input-custom border-custom-divider"><i class="fas fa-id-badge text-custom-muted"></i></span>
                      <input type="text" class="form-control input-custom border-start-0" v-model="usuarioEditando.ci" placeholder="Número de CI">
                    </div>
                  </div>
                  <div class="col-4 mb-2">
                    <label class="form-label text-xs font-weight-bold mb-1 text-custom-muted">Ext.</label>
                    <select class="form-select select-custom ps-2" v-model="usuarioEditando.extension">
                      <option value="">--</option>
                      <option v-for="ext in EXTENSIONES" :key="ext.value" :value="ext.value">{{ ext.value }}</option>
                    </select>
                  </div>

                  <div class="col-12 mb-2">
                    <label class="form-label text-xs font-weight-bold mb-1 text-custom-muted">Correo Electrónico</label>
                    <div class="input-group input-group-custom">
                      <span class="input-group-text bg-input-custom border-custom-divider"><i class="fas fa-at text-custom-muted"></i></span>
                      <input type="email" class="form-control input-custom border-start-0" v-model="usuarioEditando.email" placeholder="correo@tamep.bo">
                    </div>
                  </div>
                  <div class="col-12">
                    <label class="form-label text-xs font-weight-bold mb-1 text-custom-muted">Teléfono Móvil</label>
                    <div class="input-group input-group-custom">
                      <span class="input-group-text bg-input-custom border-custom-divider"><i class="fas fa-mobile-alt text-custom-muted"></i></span>
                      <input type="text" class="form-control input-custom border-start-0" v-model="usuarioEditando.telefono" placeholder="Ej: 71234567">
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="card border shadow-none modal-section-card border-custom-primary">
              <div class="card-body p-3">
                <h6 class="text-uppercase text-xs font-weight-bolder text-primary mb-3 d-flex align-items-center gap-2"><i class="fas fa-shield-alt"></i> Seguridad y Rol</h6>
                
                <div class="mb-3">
                  <label class="form-label text-xs font-weight-bold mb-1 text-custom-muted">Rol en el Sistema (RBAC)</label>
                  <select class="form-select select-custom select-rol font-weight-bold" v-model="usuarioEditando.rol">
                    <option v-for="r in ROLES" :key="r.value" :value="r.value">{{ r.label }}</option>
                  </select>
                  <small class="text-custom-muted text-xxs mt-1 d-block">Define los menús y acciones permitidas para este usuario.</small>
                </div>

                <div class="d-flex align-items-center justify-content-between p-2 bg-switch-container rounded mb-2 border border-custom-divider">
                  <div>
                    <h6 class="mb-0 text-sm text-title-custom">Estado de la Cuenta</h6>
                    <span class="text-xs font-weight-bold" :class="usuarioEditando.is_active ? 'text-success' : 'text-danger'">
                      {{ usuarioEditando.is_active ? 'Puede Iniciar Sesión' : 'Acceso Suspendido' }}
                    </span>
                  </div>
                  <div class="form-check form-switch mb-0">
                    <input class="form-check-input form-check-input-lg" type="checkbox" v-model="usuarioEditando.is_active" id="switchActivoModal">
                  </div>
                </div>

                <div class="d-flex align-items-center justify-content-between p-2 bg-switch-container rounded border border-custom-divider">
                  <div>
                    <h6 class="mb-0 text-sm text-title-custom">Privilegios de Staff (Django)</h6>
                    <span class="text-xs text-custom-muted">Acceso al panel interno de admin</span>
                  </div>
                  <div class="form-check form-switch mb-0">
                    <input class="form-check-input" type="checkbox" v-model="usuarioEditando.is_staff" id="switchAdminModal">
                  </div>
                </div>

              </div>
            </div>
          </div>

          <div class="col-lg-6 d-flex flex-column">
            
            <div class="card border border-custom-divider shadow-none mb-4 modal-section-card">
              <div class="card-body p-3">
                <h6 class="text-uppercase text-xs font-weight-bolder text-warning mb-3 d-flex align-items-center gap-2"><i class="fas fa-sitemap"></i> Ubicación Organizacional</h6>
                
                <div class="mb-3">
                  <label class="form-label text-xs font-weight-bold mb-1 text-custom-muted">Base / Regional</label>
                  <select class="form-select select-custom" v-model="usuarioEditando.regional_id" @change="onRegionalChange">
                    <option :value="null">-- No asignado --</option>
                    <option v-for="reg in regionales" :key="reg.id" :value="reg.id">{{ reg.nombre }}</option>
                  </select>
                </div>

                <div class="mb-3">
                  <label class="form-label text-xs font-weight-bold mb-1 text-custom-muted">Unidad / Gerencia</label>
                  <select class="form-select select-custom" v-model="usuarioEditando.gerencia_id" @change="onGerenciaChange" :disabled="!usuarioEditando.regional_id">
                    <option :value="null">{{ usuarioEditando.regional_id ? '-- Seleccionar Unidad --' : 'Requiere Regional' }}</option>
                    <option v-for="ger in gerencias" :key="ger.id" :value="ger.id">{{ ger.nombre }}</option>
                  </select>
                </div>
                
                <div class="mb-3">
                  <label class="form-label text-xs font-weight-bold mb-1 text-info">Sección / Área</label>
                  <select class="form-select select-custom border-info-light" v-model="usuarioEditando.seccion_id" @change="onSeccionChange" :disabled="!usuarioEditando.gerencia_id">
                    <option :value="null">{{ usuarioEditando.gerencia_id ? '-- Seleccionar Sección --' : 'Requiere Gerencia' }}</option>
                    <option v-for="sec in secciones" :key="sec.id" :value="sec.id">{{ sec.nombre }}</option>
                  </select>
                </div>

                <div class="mb-2">
                  <label class="form-label text-xs font-weight-bold mb-1 text-custom-muted">Cargo Funcional</label>
                  <select class="form-select select-custom select-cargo font-weight-bold" v-model="usuarioEditando.cargo_id" :disabled="!usuarioEditando.seccion_id">
                    <option :value="null">{{ usuarioEditando.seccion_id ? '-- Asignar Cargo --' : 'Requiere Sección' }}</option>
                    <option v-for="car in cargos" :key="car.id" :value="car.id">{{ car.nombre }}</option>
                  </select>
                </div>

                <div class="mt-3 pt-2 border-top">
                  <label class="form-label text-xs font-weight-bold mb-1 text-danger">
                    <i class="fas fa-file-pdf me-1"></i> Documento Respaldo General (PDF)
                  </label>
                  <div v-if="usuarioEditando.documento_respaldo_general" class="mb-2 d-flex align-items-center justify-content-between bg-light p-2 rounded">
                    <span class="text-xs text-dark text-truncate me-2"><i class="fas fa-check-circle text-success me-1"></i> Documento activo cargado</span>
                    <a :href="usuarioEditando.documento_respaldo_general" target="_blank" rel="noopener noreferrer" class="btn btn-xs btn-outline-danger mb-0">
                      <i class="fas fa-external-link-alt me-1"></i> Ver PDF
                    </a>
                  </div>
                  <input type="file" @change="handleRespaldoChange" accept=".pdf" class="form-control form-control-sm select-custom">
                  <small class="text-xxs text-custom-muted d-block mt-1">Subir o reemplazar el acta general de custodia.</small>
                </div>
              </div>
            </div>

            <div class="card shadow-none mt-auto modal-credenciales-card border border-custom-divider">
              <div class="card-body p-3">
                <div class="d-flex align-items-center mb-2">
                  <div class="icon-box-badge bg-warning text-dark me-2 shadow-sm">
                    <i class="fas fa-key text-xs"></i>
                  </div>
                  <h6 class="mb-0 text-title-custom font-weight-bolder">Credenciales de Acceso</h6>
                </div>
                <p class="text-xs text-custom-muted mb-2">El usuario ingresa al sistema utilizando:</p>
                <div class="p-2 rounded credenciales-box border border-custom-divider">
                  <div class="text-xs mb-1 d-flex justify-content-between">
                    <span class="text-custom-muted font-weight-bold">Nombre de Usuario:</span>
                    <strong class="text-title-custom">@{{ usuarioEditando.username || 'Sin usuario' }}</strong>
                  </div>
                  <div class="text-xs d-flex justify-content-between">
                    <span class="text-custom-muted font-weight-bold">Correo Registrado:</span>
                    <strong class="text-title-custom">{{ usuarioEditando.email || 'No registrado' }}</strong>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      <div class="card-footer modal-footer-custom p-3 border-top d-flex justify-content-between align-items-center border-radius-bottom-xl">
        <button class="btn btn-cancel-custom mb-0 px-4" @click="cerrarModal">
          <i class="fas fa-arrow-left me-2"></i> Volver
        </button>
        <button class="btn btn-azul-tamep btn-save-custom mb-0 px-5 btn-lg shadow-sm" @click="guardarEdicion" :disabled="guardando || cargandoUsuario">
          <i class="fas" :class="guardando ? 'fa-spinner fa-spin' : 'fa-save'"></i> 
          {{ guardando ? ' Procesando...' : ' Guardar Configuración' }}
        </button>
      </div>

    </div>
  </div>
</template>

<style scoped>
/* PALETA TAMEP Y LAYOUT */
.header-tamep { background: linear-gradient(135deg, #003366 0%, #001d3d 100%); }
.border-radius-top { border-top-left-radius: 12px; border-top-right-radius: 12px; }
.logo-container-left { background: white; padding: 5px 12px; border-radius: 8px; }
.logo-img { max-height: 35px; }

.text-azul-tamep { color: #003366 !important; }
.bg-azul-tamep { background-color: #003366 !important; }
.bg-gradient-dark-tamep { background: linear-gradient(135deg, #001d3d 0%, #003366 100%); }
.btn-azul-tamep {
  background: linear-gradient(310deg, #003366 0%, #00509d 100%);
  color: white;
  border: none;
  transition: all 0.3s ease;
}
.btn-azul-tamep:hover:not(:disabled) {
  background: linear-gradient(310deg, #002244 0%, #003366 100%);
  transform: translateY(-2px);
  box-shadow: 0 7px 14px rgba(0,51,102,0.2) !important;
}

/* UTILIDADES GLOBALES */
.app-transition { transition: all 0.3s ease; }
.min-vh-50 { min-height: 50vh; }
.cursor-pointer { cursor: pointer; }
.bg-gray-50 { background-color: #f8f9fa !important; }
.bg-section-custom { background-color: #f8f9fa; }
.border-radius-top-xl { border-top-left-radius: 1rem; border-top-right-radius: 1rem; }
.border-radius-bottom-xl { border-bottom-left-radius: 1rem; border-bottom-right-radius: 1rem; }
.border-info-light { border-color: #17a2b880 !important; }

/* Variables de Modo Claro */
.card-custom { background-color: #ffffff; transition: background-color 0.3s ease; }
.card-header-custom { background-color: #ffffff; transition: background-color 0.3s ease; }
.card-body-custom { background-color: #ffffff; transition: background-color 0.3s ease; }
.bg-pagination-custom { background-color: #f8f9fa !important; transition: background-color 0.3s ease; }
.border-custom-divider { border-color: #e9ecef !important; transition: border-color 0.3s ease; }

.text-title-custom { color: #344767; transition: color 0.3s ease; }
.text-custom-muted { color: #8392ab; transition: color 0.3s ease; }

.input-custom, .select-custom { 
  background-color: #ffffff; 
  color: #495057; 
  border-color: #d2d6da; 
  transition: all 0.3s ease; 
}
.bg-input-custom { background-color: #ffffff; border-color: #d2d6da; }

/* ANIMACIONES Y EFECTOS */
.transition-row { transition: background-color 0.2s ease; }
.transition-row:hover { background-color: #f8f9fa !important; }
.hover-lift { transition: transform 0.2s ease; }
.hover-lift:hover { transform: translateY(-2px); }
.hover-bg-light:hover { background-color: rgba(0,0,0,0.05); }

/* CUSTOM SWITCHES */
.form-check-input-lg { width: 3rem !important; height: 1.5rem !important; }

/* PAGINACIÓN CUSTOM */
.pagination-primary .page-item.active .page-link {
  background-color: #003366;
  border-color: #003366;
  color: white;
  box-shadow: 0 3px 5px rgba(0,51,102,0.3);
}

/* OVERLAY Y MODAL BASE (LIGHT MODE) */
.modal-overlay {
  position: fixed; top: 0; left: 0; width: 100%; height: 100%;
  background: rgba(0, 10, 20, 0.65); backdrop-filter: blur(6px);
  display: flex; align-items: center; justify-content: center; z-index: 9999;
}
.modal-card { width: 900px; max-width: 95vw; max-height: 95vh; display: flex; flex-direction: column; }
.modal-card-custom { background-color: #ffffff; border: 1px solid #e2e8f0; }
.modal-header-custom { background-color: #f8fafc; border-bottom: 1px solid #e2e8f0; }
.modal-body-custom { background-color: #f1f5f9; }
.modal-body-scroll { overflow-y: auto; overflow-x: hidden; max-height: calc(95vh - 160px); }
.modal-body-scroll::-webkit-scrollbar { width: 6px; }
.modal-body-scroll::-webkit-scrollbar-track { background: transparent; }
.modal-body-scroll::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 10px; }
.modal-section-card { background-color: #ffffff; border-color: #cbd5e1; }
.modal-footer-custom { background-color: #ffffff; border-top: 1px solid #e2e8f0; }

.bg-input-custom { background-color: #f8fafc; color: #64748b; }
.input-custom, .select-custom { background-color: #ffffff; color: #1e293b; border-color: #cbd5e1; }
.input-custom:focus, .select-custom:focus { border-color: #003366; box-shadow: 0 0 0 0.2rem rgba(0, 51, 102, 0.15); }

.bg-switch-container { background-color: #f8fafc; }
.select-rol { background-color: #e0f2fe; color: #0369a1; border-color: #38bdf8; }
.select-cargo { background-color: #fef3c7; color: #b45309; border-color: #f59e0b; }

/* ICON BOXES Y ALINEACIÓN DE ÍCONOS */
.icon-box-modal {
  width: 44px; height: 44px; min-width: 44px; min-height: 44px;
  display: flex; align-items: center; justify-content: center;
  border-radius: 50%; flex-shrink: 0;
}
.icon-box-badge {
  width: 32px; height: 32px; min-width: 32px; min-height: 32px;
  display: flex; align-items: center; justify-content: center;
  border-radius: 50%; flex-shrink: 0;
}
.input-group-custom .input-group-text {
  display: flex; align-items: center; justify-content: center;
  min-width: 40px; padding: 0.45rem 0.65rem;
}

.btn-cancel-custom { background-color: #f1f5f9; color: #475569; border: 1px solid #cbd5e1; }
.btn-cancel-custom:hover { background-color: #e2e8f0; color: #1e293b; }

</style>

<style>
/* =========================================
   SOPORTE COMPLETO MODO OSCURO (DARK MODE)
   ========================================= */
.dark-version .card-custom, .dark-mode .card-custom { background-color: #1e1e1e !important; border: 1px solid #333 !important; }
.dark-version .card-header-custom, .dark-mode .card-header-custom { background-color: #1e1e1e !important; border-bottom: 1px solid #333 !important; }
.dark-version .card-body-custom, .dark-mode .card-body-custom { background-color: #1e1e1e !important; }
.dark-version .bg-pagination-custom, .dark-mode .bg-pagination-custom { background-color: #121212 !important; border-top: 1px solid #333 !important; }
.dark-version .border-custom-divider, .dark-mode .border-custom-divider { border-color: #333333 !important; }
.dark-version .bg-section-custom, .dark-mode .bg-section-custom { background-color: #1a1a1a !important; border-color: #333 !important; }
.dark-version .bg-gray-50, .dark-mode .bg-gray-50 { background-color: #1a1a1a !important; border-color: #333 !important; }

.dark-version .text-title-custom, .dark-mode .text-title-custom { color: #ffffff !important; }
.dark-version .text-custom-muted, .dark-mode .text-custom-muted { color: #cbd5e1 !important; }
.dark-version .text-primary, .dark-mode .text-primary { color: #38bdf8 !important; }
.dark-version .text-warning, .dark-mode .text-warning { color: #fbbf24 !important; }
.dark-version .text-dark, .dark-mode .text-dark { color: #ffffff !important; }
.dark-version .text-secondary, .dark-mode .text-secondary { color: #94a3b8 !important; }

/* Tabla Oscura */
.dark-version .table-custom th, .dark-mode .table-custom th { color: #aaaaaa !important; }
.dark-version .table-custom td, .dark-mode .table-custom td { color: #e0e0e0 !important; border-color: #333 !important; }
.dark-version .transition-row:hover, .dark-mode .transition-row:hover { background-color: #2a2a2a !important; }
.dark-version .bg-badge-custom, .dark-mode .bg-badge-custom { background-color: #333 !important; color: #fff !important; border-color: #444 !important; }

/* Componentes del Modal en Oscuro */
.dark-version .modal-card-custom, .dark-mode .modal-card-custom { background-color: #1e293b !important; border: 1px solid #334155 !important; box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.7) !important; }
.dark-version .modal-header-custom, .dark-mode .modal-header-custom { background-color: #0f172a !important; border-bottom: 1px solid #334155 !important; }
.dark-version .modal-body-custom, .dark-mode .modal-body-custom { background-color: #090d16 !important; }
.dark-version .modal-section-card, .dark-mode .modal-section-card { background-color: #1e293b !important; border-color: #334155 !important; }
.dark-version .modal-footer-custom, .dark-mode .modal-footer-custom { background-color: #0f172a !important; border-top: 1px solid #334155 !important; }

.dark-version .modal-credenciales-card, .dark-mode .modal-credenciales-card { background-color: #0f172a !important; border-color: #334155 !important; }
.dark-version .credenciales-box, .dark-mode .credenciales-box { background-color: #1e293b !important; border-color: #334155 !important; }

.dark-version .btn-close-modal, .dark-mode .btn-close-modal { color: #94a3b8 !important; }
.dark-version .btn-close-modal:hover, .dark-mode .btn-close-modal:hover { background-color: #334155 !important; color: #ffffff !important; }
.dark-version .btn-cancel-custom, .dark-mode .btn-cancel-custom { background-color: #334155 !important; color: #f1f5f9 !important; border: 1px solid #475569 !important; }
.dark-version .btn-cancel-custom:hover, .dark-mode .btn-cancel-custom:hover { background-color: #475569 !important; color: #ffffff !important; }

/* Formularios, Inputs y Selects en Oscuro */
.dark-version .input-custom,
.dark-version .select-custom,
.dark-mode .input-custom,
.dark-mode .select-custom { background-color: #0f172a !important; color: #f8fafc !important; border-color: #334155 !important; }

.dark-version .input-custom::placeholder,
.dark-mode .input-custom::placeholder { color: #64748b !important; }

.dark-version .bg-input-custom, .dark-mode .bg-input-custom { background-color: #1e293b !important; color: #94a3b8 !important; border-color: #334155 !important; }

/* Legibilidad crítica para opciones dentro de los elementos SELECT en MODO OSCURO */
.dark-version select option,
.dark-mode select option { background-color: #0f172a !important; color: #f8fafc !important; }

/* Estado Disabled Generico */
.dark-version select:disabled, 
.dark-version input:disabled,
.dark-mode select:disabled, 
.dark-mode input:disabled { background-color: #1e293b !important; color: #64748b !important; border-color: #334155 !important; opacity: 0.7; }

/* Switches y selectores especiales */
.dark-version .bg-switch-container, .dark-mode .bg-switch-container { background-color: #0f172a !important; border-color: #334155 !important; }
.dark-version .select-rol, .dark-mode .select-rol { background-color: #0369a1 !important; color: #ffffff !important; border-color: #38bdf8 !important; }
.dark-version .select-cargo, .dark-mode .select-cargo { background-color: #78350f !important; color: #fef3c7 !important; border-color: #f59e0b !important; }
.dark-version .border-custom-primary, .dark-mode .border-custom-primary { border-color: #38bdf8 !important; }
</style>